import React from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import {
  Modal,
  ModalBody,
  ModalHeader,
  Label,
  Input,
} from 'reactstrap';

import images from 'assets/images';
import { ReactComponent as CloseButtonSvg } from 'assets/images/location/close-button-svg.svg';

import Button from 'components/App/Button';

import INITIAL_VALUE from 'constants/initialValue';

import styles from './ModalEditPolicyDeposit.module.scss';

const DISPLAY_CHILDREN_BY_GROUP = {
  INTERNATIONAL: ['MASTERCARD', 'VISA', 'JCB', 'AMERICAN EXPRESS'],
  DOMESTIC: ['NAPAS'],
  QR: ['MOMO', 'VNPAY', 'ZALOPAY', 'SHOPEEPAY', 'PAYOO', 'PAYPAL'],
  CASH: ['CASH'],
};

const GROUP_LABELS = {
  INTERNATIONAL: 'Thẻ quốc tế',
  DOMESTIC: 'Thẻ nội địa',
  QR: 'Ví điện tử',
  CASH: 'Khác',
};

const CHILD_LABELS = {
  MASTERCARD: 'Mastercard',
  VISA: 'Visa',
  JCB: 'JCB',
  'AMERICAN EXPRESS': 'American Express',
  NAPAS: 'Napas',
  MOMO: 'MoMo',
  VNPAY: 'VNPAY',
  ZALOPAY: 'Zalopay',
  SHOPEEPAY: 'ShopeePay',
  PAYOO: 'Payoo',
  PAYPAL: 'Paypal',
  CASH: 'Tiền mặt',
};

const getPaymentImage = (name) => {
  const img = images[name];
  if (!img) return null;
  return img.default || img;
};

const getChildLabel = (child) => CHILD_LABELS[child.name];

const isChildSelected = (savedChildren, childName) => (
  (savedChildren || []).some((child) => (child.name || child) === childName)
);

const buildPaymentMethodsFormValues = (defaultPaymentMethods, savedPaymentMethods) => (
  defaultPaymentMethods.map((item) => {
    const allowedChildren = DISPLAY_CHILDREN_BY_GROUP[item.name] || [];
    const savedChildren = savedPaymentMethods.find((p) => p.group === item.name)?.children;

    return {
      ...item,
      children: (item.children || [])
        .filter((child) => allowedChildren.includes(child.name))
        .map((child) => ({
          ...child,
          isChecked: isChildSelected(savedChildren, child.name),
        })),
    };
  })
);

function ModalEditPolicyDeposit(props) {
  const {
    isOpen,

    defaultPaymentMethods,
    paymentMethods,

    actionGetPlaceDetail,
    actionEditDepositPolicy,
    onToggleModal,
  } = props;

  const location = useLocation();
  const placeId = new URLSearchParams(location.search).get('place');

  const onCallBackSave = React.useCallback(() => {
    onToggleModal();
    actionGetPlaceDetail({ placeId });
  }, [actionGetPlaceDetail, onToggleModal, placeId]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      paymentMethods: [],
    },
    onSubmit: (values) => {
      const result = {
        paymentMethods: values.paymentMethods
          .map((item) => ({
            group: item.name,
            children: (item.children || [])
              .filter((child) => child.isChecked)
              .map((child) => ({
                name: child.name,
                bin: child.bin,
              })),
          }))
          .filter((item) => item.children.length > 0),
        placeId,
        onCallBackSave,
      };

      actionEditDepositPolicy(result);
    },
  });

  const handleChangeChild = React.useCallback(({ target: { checked, name } }) => {
    validation.setValues((prevState) => ({
      paymentMethods: prevState.paymentMethods.map((item) => ({
        ...item,
        children: (item.children || []).map((child) => {
          if (child.name === name) {
            return {
              ...child,
              isChecked: checked,
            };
          }
          return child;
        }),
      })),
    }));
  }, []);

  React.useEffect(() => {
    if (!isOpen || defaultPaymentMethods.length === 0) return;

    validation.setValues({
      paymentMethods: buildPaymentMethodsFormValues(defaultPaymentMethods, paymentMethods),
    });
  }, [isOpen, paymentMethods, defaultPaymentMethods]);

  const selectedChildren = React.useMemo(
    () => validation.values.paymentMethods.flatMap(
      (item) => (item.children || []).filter((child) => child.isChecked),
    ),
    [validation.values.paymentMethods],
  );

  return (
    <div className={styles.Component}>
      <Modal
        fade
        size="lg"
        isOpen={isOpen}
        toggle={onToggleModal}
        className={styles.Modal}
      >
        <ModalHeader
          toggle={onToggleModal}
          className={styles.ModalHeader}
          close={(
            <button className={styles.ButtonClose} onClick={onToggleModal} type="button">
              <CloseButtonSvg />
            </button>
          )}
        />
        <form onSubmit={validation.handleSubmit}>
          <ModalBody className={styles.ModalBody}>
            <Label className="Label">
              Lựa chọn thanh toán của khách
            </Label>

            <div style={{ marginTop: 20 }}>
              {validation.values.paymentMethods.map((group) => (
                <React.Fragment key={group.name}>
                  <div className="Block__Container">
                    <div className="Block__Title">
                      {GROUP_LABELS[group.name]}
                    </div>

                    <div className="Block__Content__Wrapper">
                      <div className="Block__Options">
                        {(group.children || []).map((child) => {
                          const imageSrc = getPaymentImage(child.name);

                          return (
                            <label
                              key={child.name}
                              htmlFor={child.name}
                              className="Block__Checkbox"
                            >
                              <div className="Block__Checkbox__Wrapper">
                                <Input
                                  name={child.name}
                                  id={child.name}
                                  type="checkbox"
                                  className="Block__Checkbox__Input"
                                  onChange={handleChangeChild}
                                  checked={!!child.isChecked}
                                />

                                {imageSrc && (
                                <img
                                  className="Block__Checkbox__Image"
                                  src={imageSrc}
                                  alt={getChildLabel(child)}
                                />
                                )}

                                <div className="Block__Checkbox__Name">
                                  {getChildLabel(child)}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </div>

            <div className="Block__Policy__Wrapper">
              <div className="Block__Title" style={{ fontWeight: 600 }}>
                Tóm tắt chính sách
              </div>

              <div className="Block__Policy__Summary">
                {selectedChildren.map((child) => {
                  const imageSrc = getPaymentImage(child.name);

                  return (
                    <div key={child.name} className="Block__Policy__Item">
                      {imageSrc ? (
                        <img
                          className="Block__Policy__Item__Image"
                          src={imageSrc}
                          alt={getChildLabel(child)}
                        />
                      ) : (
                        <p className="Block__Policy__Item__Text">
                          {getChildLabel(child)}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="Button__Wrapper">
              <Button
                classes="Button Button__Cancel"
                onClick={onToggleModal}
              >
                Huỷ
              </Button>

              <Button classes="Button Button__Save" type="submit">
                Lưu
              </Button>
            </div>
          </ModalBody>
        </form>
      </Modal>
    </div>
  );
}

ModalEditPolicyDeposit.propTypes = {
  isOpen: PropTypes.bool.isRequired,

  defaultPaymentMethods: PropTypes.instanceOf(Array),
  paymentMethods: PropTypes.instanceOf(Array),

  actionGetPlaceDetail: PropTypes.func.isRequired,
  actionEditDepositPolicy: PropTypes.func.isRequired,
  onToggleModal: PropTypes.func.isRequired,
};

ModalEditPolicyDeposit.defaultProps = {
  defaultPaymentMethods: INITIAL_VALUE.ARRAY,
  paymentMethods: INITIAL_VALUE.ARRAY,
};

export default React.memo(ModalEditPolicyDeposit);
